import React, { useState, useEffect, useCallback } from "react";
import { motion, useAnimation } from "framer-motion";
import { useCampaignPage } from "~/store";
import { BtnUi, PreferencesNoticeUi, SignInDialogUi } from "~/components";
import styles from "./BubblePage.module.css";
import { useNavigate } from "react-router-dom";
import { usePassions } from "../../hooks/usePassions2";
import { useAcid } from "../../hooks/useAcid";
import { allPassions as items, passionsParams } from "../../utils/passionsData";

export const JigglingCircles = () => {
  const cid = "share";
  const navigate = useNavigate();
  const { acid } = useAcid();
  const {
    tapPassion,
    isAnyPassionSelected,
    getPassionValue,
    savePassionsToServer,
    getSelectedPassionsAsArray,
    getSelectedPassionsAsFlatArray,
  } = usePassions("bubbles3");
  const [showSignInDialog, setShowSignInDialog] = useState(false);
  const { setShowPageLoader } = useCampaignPage();

  const tapStorage = passionsParams.map((p) => getPassionValue(p));
  const circleCount = items.length;
  const [activeCircles, setActiveCircles] = useState([]);
  const [initialAnimationState] = useState(() => {
    return tapStorage.map((value) => {
      switch (value) {
        case 2:
          return "tapped2idle";
        case 1:
          return "tapped1idle";
        default:
          return "idle";
      }
    });
  });
  const [initialIconAnimationState] = useState(() => {
    return tapStorage.map((value) => {
      switch (value) {
        case 2:
          return "visible2";
        case 1:
          return "visible1";
        default:
          return "hidden";
      }
    });
  });
  const controls = Array(circleCount)
    .fill()
    .map(() => useAnimation());

  const passionsPerRow = 3;

  const jiggleVariants = {
    idle: {
      scale: 1,
      rotate: 0,
      width: 70,
      height: 70,
      backgroundColor: "#ffffff",
      color: "var(--default-color)",
    },
    jiggle: {
      scale: [1, 1.1, 0.9, 1.05, 0.95, 1],
      rotate: [0, 5, -5, 3, -3, 0],
      transition: { duration: 1 },
    },
    tapped1idle: {
      width: 70 * 1.25,
      height: 70 * 1.25,
      padding: 2,
      backgroundColor: "var(--default-color)",
      color: "#ffffff",
    },
    tapped1: {
      width: 70 * 1.25,
      height: 70 * 1.25,
      padding: 2,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 8,
      },
      backgroundColor: "var(--default-color)",
      color: "#ffffff",
    },
    tapped2idle: {
      width: 80 * 1.5,
      height: 80 * 1.5,
      padding: 0,
      backgroundColor: "var(--default-color)",
      color: "#ffffff",
    },
    tapped2: {
      width: 80 * 1.5,
      height: 80 * 1.5,
      padding: 0,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 8,
      },
      backgroundColor: "var(--default-color)",
      color: "#ffffff",
    },
  };

  const iconVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible1: { opacity: 1, scale: 1, height: 30, width: 30 },
    visible2: { opacity: 1, scale: 1, height: 50, width: 50 },
  };

  function chunkArray(arr, chunkSize) {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      chunks.push(arr.slice(i, i + chunkSize));
    }
    return chunks;
  }

  useEffect(() => {
    const animateNextCircle = () => {
      if (activeCircles.length < 2) {
        const availableCircles = passionsParams.reduce((acc, p, index) => {
          const taps = getPassionValue(p);
          return taps === 0 ? [...acc, index] : acc;
        }, []);

        if (availableCircles.length === 0) return;

        const nextCircle =
          availableCircles[Math.floor(Math.random() * availableCircles.length)];

        setActiveCircles((prev) => [...prev, nextCircle]);
        controls[nextCircle].start("jiggle").then(() => {
          setActiveCircles((prev) => prev.filter((c) => c !== nextCircle));
        });
      }
    };

    const intervalId = setInterval(animateNextCircle, 1000);
    return () => clearInterval(intervalId);
  }, [activeCircles, controls, getPassionValue]);

  const handleTap = (index) => {
    const passion = passionsParams[index];
    const currentTaps = tapPassion(passion, 2);

    if (currentTaps === 0) {
      controls[index].start("idle");
    } else {
      controls[index].start(currentTaps === 1 ? "tapped1" : "tapped2");
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!acid) {
      setShowSignInDialog(true);
      return;
    }

    await savePassionsToServer(acid);
    navigate("/share3/explore");
  }, [
    acid,
    setShowSignInDialog,
    getSelectedPassionsAsArray,
    savePassionsToServer,
    navigate,
  ]);

  const onCloseDialog = useCallback(
    () => setShowSignInDialog(false),
    [setShowSignInDialog],
  );

  const onSubmitDialog = useCallback(() => {
    onCloseDialog();
    setShowPageLoader(true);
  }, [onCloseDialog, setShowPageLoader]);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <div className={styles.bubbles}>
        {chunkArray(items, passionsPerRow).map((row, rowIndex) => (
          <motion.div key={rowIndex} className="bubble-container" layout>
            {row.map((p, pIndex) => {
              const i = pIndex + passionsPerRow * rowIndex;
              const passion = passionsParams[i];
              const taps = getPassionValue(passion);
              return (
                <motion.div key={pIndex} className="circle-wrapper" layout>
                  <motion.div
                    className={`circle custom_click_track`}
                    data-custom_click_track_value={`Bubbles|${p.label}|internal`}
                    variants={jiggleVariants}
                    initial={initialAnimationState[i]}
                    animate={controls[i]}
                    onTap={() => handleTap(i)}
                  >
                    {taps > 0 && (
                      <motion.img
                        src={p.icon}
                        variants={iconVariants}
                        initial={initialIconAnimationState[i]}
                        animate={
                          taps === 1
                            ? "visible1"
                            : taps === 2
                              ? "visible2"
                              : "hidden"
                        }
                        alt=""
                      />
                    )}
                    <span className={styles.motionLabel}>{p.label}</span>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        ))}
        <style>{`
          .bubble-container {
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            width: 100%;
          }

          .circle-wrapper {
            display: block;
            padding: 8px 12px;
          }

          .circle {
            width: 80px;
            height: 80px;
            background-color: #ffffff;
            border: 1px solid var(--default-color);
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            color: var(--default-color);
            cursor: pointer;
          }
        `}</style>
      </div>

      <BtnUi
        className={`${styles.explore} custom_click_track`}
        data-custom_click_track_value={`Bubbles Footer|Save Continue|internal`}
        onClick={handleSubmit}
        disabled={!isAnyPassionSelected()}
      >
        Save & Continue
      </BtnUi>
      <PreferencesNoticeUi className={styles.notice} />
      {showSignInDialog && (
        <SignInDialogUi
          onClose={onCloseDialog}
          onSubmit={onSubmitDialog}
          selectedPassions={getSelectedPassionsAsFlatArray()}
          cid={cid}
        />
      )}
    </div>
  );
};
